import { useImperativeHandle, forwardRef, useRef, useState } from "react"

export interface MagnifierData {
    pixelX: number
    pixelY: number
    sourceCanvas: HTMLCanvasElement | HTMLImageElement | HTMLVideoElement
}

export interface MagnifierHandle {
    update: (data: MagnifierData) => void
    hide: () => void
}

interface MagnifierProps {
    zoom?: number
    size?: number
    borderColor?: string
    borderRadius?: number
    className?: string
}

export const Magnifier = forwardRef<MagnifierHandle, MagnifierProps>(
    ({ zoom = 5, size = 100, className = "" }, ref) => {
        const [visible, setVisible] = useState(false)
        const canvasRef = useRef<HTMLCanvasElement>(null)

        useImperativeHandle(ref, () => ({
            update: (data: MagnifierData) => {
                if (!visible) setVisible(true)

                const canvas = canvasRef.current
                if (!canvas) return

                const dpr = window.devicePixelRatio || 1
                const logicalWidth = size
                const logicalHeight = size

                if (canvas.width !== logicalWidth * dpr || canvas.height !== logicalHeight * dpr) {
                    canvas.width = logicalWidth * dpr
                    canvas.height = logicalHeight * dpr
                    const ctx = canvas.getContext("2d", { alpha: false })
                    if (ctx) ctx.scale(dpr, dpr)
                }

                const ctx = canvas.getContext("2d", { alpha: false })
                if (!ctx) return

                ctx.imageSmoothingEnabled = false

                const sourceSize = size / zoom

                ctx.clearRect(0, 0, size, size)
                ctx.drawImage(
                    data.sourceCanvas,
                    Math.floor(data.pixelX - sourceSize / 2),
                    Math.floor(data.pixelY - sourceSize / 2),
                    sourceSize,
                    sourceSize,
                    0,
                    0,
                    size,
                    size
                )

                const centerOffset = (size - zoom) / 2

                ctx.lineWidth = 1.5
                ctx.strokeStyle = "#FFFFFF"

                ctx.beginPath()
                ctx.roundRect(centerOffset, centerOffset, zoom, zoom, 3)
                ctx.stroke()
            },
            hide: () => setVisible(false),
        }))

        if (!visible) return null

        return (
            <div
                className={`overflow-hidden bg-white shadow-xl ${className}`}
                style={{ width: size, height: size }}
            >
                <canvas ref={canvasRef} style={{ width: size, height: size }} />
            </div>
        )
    }
)

Magnifier.displayName = "Magnifier"
